import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone, Accept, FileRejection } from 'react-dropzone';
import { useForm, Controller } from 'react-hook-form';
import Compressor from 'compressorjs';
import { GlobalData, Media } from '../../../types';
import global from '../../../styles/index.module.scss';
import styles from './Step4.module.scss';
import { Layout } from '../../../components/common/Layout/Layout';
import { StepHeader } from '../../../components/common/StepHeader/StepHeader';
import { ChangeButtonLayout } from '../../../components/common/ChangeButtonLayout/ChangeButtonLayout';
import { redirectTo404 } from '../../../utils/redirectTo404';

interface Step4Props {
  data: GlobalData[];
  firstCollectionHandle: string | null;
  secondCollectionHandle: string | null;
  productHandle: string | null;
  expireText?: string;
}

interface FormData {
  [key: string]: File | null | File[];
}

export const Step4: React.FC<Step4Props> = ({
  data,
  firstCollectionHandle,
  secondCollectionHandle,
  productHandle,
  expireText
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const compressImage = (file: File, callback: (compressedFile: File) => void) => {
    const originalSizeKB = file.size / 1024;
    console.log(`Original file size: ${originalSizeKB.toFixed(2)} KB`);
    new Compressor(file, {
      quality: 0.6,
      maxWidth: 1920,
      maxHeight: 1920,
      success: (result) => {
        if (result instanceof Blob) {
          const compressedFile = blobToFile(result, file.name);
          const compressedSizeKB = compressedFile.size / 1024;
          console.log(`Compressed file size: ${compressedSizeKB.toFixed(2)} KB`);
          console.log(`Size reduction: ${(originalSizeKB - compressedSizeKB).toFixed(2)} KB`);
          console.log(`Compression ratio: ${(compressedSizeKB / originalSizeKB * 100).toFixed(2)}%`);
          callback(compressedFile);
        }
      },
      error: (err) => {
        console.error('Error compressing the image:', err);
        setError('画像の圧縮に失敗しました。');
      },
    });
  };

  function blobToFile(theBlob: Blob, fileName: string): File {
    return new File([theBlob], fileName, {
      type: theBlob.type,
      lastModified: Date.now(),
    });
  }

  const onDropNormal = useCallback(
    (acceptedFiles: File[], key: string) => {
      if (acceptedFiles.length > 0) {
        compressImage(acceptedFiles[0], (compressedFile) => {
          setValue(key, compressedFile, { shouldValidate: true });
          setError(null);
        });
      }
    },
    [setValue]
  );

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      setError(
        '受け付けていないファイル形式です。jpeg, jpg, png のいずれかの形式をアップロードしてください。'
      );
    }
  }, []);

  const handleNextStep = (data: FormData) => {
    // 現在のURLパラメータを取得
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('step', '5');
    currentParams.set('firstcollectionhandle', firstCollectionHandle || '');
    currentParams.set('secondcollectionhandle', secondCollectionHandle || '');
    currentParams.set('producthandle', productHandle || '');
    const linkPath = `/?${currentParams.toString()}`;
    
    navigate(linkPath, { state: { images: data } });
  };

  const accept: Accept = {
    'image/jpeg': [],
    'image/png': [],
  };

  const createDropzone = (field: string) => {
    const { getRootProps, getInputProps, isDragReject, fileRejections } =
      useDropzone({
        accept,
        onDrop: (acceptedFiles) => onDropNormal(acceptedFiles, field),
        onDropRejected,
      });
    return (
      <div
        {...getRootProps()}
        className={`${styles.uploadPlaceholder} ${
          isDragReject || errors[field] ? styles.reject : ''
        }`}
      >
        <input {...getInputProps()} />
        <Controller
          name={field}
          control={control}
          rules={{ required: '画像の登録は必須です。' }}
          render={({ field: { value } }) => (
            <>
              {value ? (
                <div className={styles.uploadImg}>
                  {value instanceof Blob ? (
                    <img
                      src={URL.createObjectURL(value)}
                      alt={`uploaded ${field}`}
                      className={styles.uploadedImage}
                    />
                  ) : (
                    <p className={styles.errorText}>無効なファイル形式です。</p>
                  )}
                  <p className={styles.uploadImgText}>画像を変更する</p>
                </div>
              ) : (
                <label className={styles.uploadText}>画像登録【必須】</label>
              )}
            </>
          )}
        />
        {fileRejections.length > 0 && (
          <p className={styles.errorText}>{error}</p>
        )}
      </div>
    );
  };

  const [additionalFiles, setAdditionalFiles] = useState<{ [key: string]: File | null }>({
    additional: null,
  });

  const onDropAdditional = useCallback((acceptedFiles: File[], field: string) => {
    if (acceptedFiles.length > 0) {
      compressImage(acceptedFiles[0], (compressedFile) => {
        setAdditionalFiles((prevFiles) => {
          const newFiles = { ...prevFiles, [field]: compressedFile };
          const existingFilesCount = Object.values(newFiles).filter(file => file !== null).length;
          if (existingFilesCount < 3) {
            const nextField = `image${existingFilesCount + 1}`;
            newFiles[nextField] = null;
          }
          setValue('additional', Object.values(newFiles).filter((file): file is File => file !== null), { shouldValidate: true });
          setError(null);
          return newFiles;
        });
      });
    }
  }, [setValue]);

  const AdditionalDropzone = ({ field }: { field: string }) => {
    const { getRootProps, getInputProps, isDragReject, fileRejections } = useDropzone({
      accept,
      onDrop: (acceptedFiles) => onDropAdditional(acceptedFiles, field),
      onDropRejected,
    });

    return (
      <div className={styles.mediaContainer}>
        <div
          {...getRootProps()}
          className={`${styles.uploadPlaceholder} ${styles.additional} ${
            isDragReject || errors[field] ? styles.reject : ''
          }`}
        >
          <input {...getInputProps()} />
          {additionalFiles[field] ? (
            <div className={styles.uploadImg}>
              <img
                src={URL.createObjectURL(additionalFiles[field]!)}
                alt={`追加画像${field}`}
                className={styles.uploadedImage}
              />
              <p className={styles.uploadImgText}>画像を変更する</p>
            </div>
          ) : (
            <p className={styles.uploadText}>画像を登録する</p>
          )}
          {fileRejections.length > 0 && (
            <p className={styles.errorText}>{error}</p>
          )}
        </div>
      </div>
    );
  };

  const AdditionalMustDropzone = ({ field }: { field: string }) => {
    const { getRootProps, getInputProps, isDragReject, fileRejections } = useDropzone({
      accept,
      onDrop: (acceptedFiles) => onDropNormal(acceptedFiles, field),
      onDropRejected,
    });

    console.log('fileRejections:', fileRejections);

    return (
      <div className={styles.mediaContainer}>
        <div
          {...getRootProps()}
          className={`${styles.uploadPlaceholder} ${styles.additional} ${
            isDragReject || errors[field] ? styles.reject : ''
          }`}
        >
          <input {...getInputProps()} />
          <Controller
            name={field}
            control={control}
            rules={{ required: '画像の登録は必須です。' }}
            render={({ field: { value } }) => (
              <>
                {value ? (
                  <div className={styles.uploadImg}>
                    {value instanceof Blob ? (
                      <img
                        src={URL.createObjectURL(value)}
                        alt={`追加画像${field}`}
                        className={styles.uploadedImage}
                      />
                    ) : (
                      <p className={styles.errorText}>無効なファイル形式です。</p>
                    )}
                    <p className={styles.uploadImgText}>画像を変更する</p>
                  </div>
                ) : (
                  <p className={styles.uploadText}>画像を登録する</p>
                )}
              </>
            )}
          />
          {fileRejections.length > 0 && (
            <p className={styles.errorText}>{error}</p>
          )}
        </div>
      </div>
    );
  };

  if (!firstCollectionHandle || !secondCollectionHandle || !productHandle) {
    redirectTo404();
    return null;
  }

  const collectionData = data.find(
    (d) => d.first_collection.handle === firstCollectionHandle
  );
  
  // first_otherのデータはオプショナル
  const collectionOtherData = data.find((d) => d.first_collection.handle === 'first_other');
  
  const secondCollectionData =
    secondCollectionHandle === 'second_other'
      ? collectionOtherData?.second_collections?.[0]
      : collectionData?.second_collections.find(
          (sc) => sc.second_collection.handle === secondCollectionHandle
        );
  
  const productData =
    productHandle === 'product_other'
      ? collectionOtherData?.second_collections?.[0]?.products?.[0]
      : secondCollectionData?.products.find(
          (p) => p.product.handle === productHandle
        );

  return (
    <div className={styles.step4}>
      <Layout>
        <ChangeButtonLayout
          stepNumber={3}
          firstCollectionHandle={firstCollectionHandle}
          firstCollectionTitle={collectionData?.first_collection.title}
          secondCollectionHandle={secondCollectionHandle}
          secondCollectionTitle={secondCollectionData?.second_collection.title}
          productHandle={productHandle}
          productTitle={productData?.product.title}
        />
      </Layout>
      <Layout>
        <StepHeader
          stepNumber={4}
          title="修理したいアイテムの画像をアップしてください"
          firstcollectionhandle={firstCollectionHandle}
        />
        <p>※JPG、PNG形式</p>
        <p>※ご質問は1送信につき1アイテムでお願いいたします。複数のアイテムを相談したい場合は（例：靴2足の場合、バッグ2個など）それぞれ別に質問を送ってください。</p>
        <p>※他のアイテムの画像をアップしないでください。</p>
        <p>※画像がアップロードできない場合は、一度撮影しカメラロールに保存してからアップロードしてください。</p>
      </Layout>
      <form onSubmit={(event) => void handleSubmit(handleNextStep)(event)}>
        <div className={styles.imageUploadSection}>
          {productData?.product.media &&
            productData?.product.media.map((mediaItem: Media, index: number) => {
              const field = `image${index + 1}`;
              return (
                <div key={mediaItem.id} className={styles.imageUpload}>
                  <Layout>
                    <p className={styles.imageTitle}>{mediaItem.alt}</p>
                    <div className={styles.mediaContainer}>
                      <div>
                        <img
                          src={mediaItem.src}
                          alt={mediaItem.alt ?? undefined}
                          className={styles.mediaImage}
                        />
                      </div>
                      {createDropzone(field)}
                    </div>
                    {errors[field] && (
                      <p className={styles.errorText}>
                        {errors[field]?.message}
                      </p>
                    )}
                  </Layout>
                </div>
              );
            })}
          {/* 追加画像フィールド */}
          {productData?.product.media && (
            Object.keys(additionalFiles).map((field) => (
              <div key={field} className={styles.imageUpload}>
                <Layout>
                  <p className={styles.imageTitle}>追加画像</p>
                  <AdditionalDropzone field={field} />
                </Layout>
              </div>
            ))
          )}
          {/* productData?.product.media が存在しない場合の画像フィールド */}
          {!productData?.product.media && (
            Array.from({ length: 4 }).map((_, index) => {
              const field = `additional[${index}]`;
              return (
                <div key={field} className={styles.imageUpload}>
                  <Layout>
                    <p className={styles.imageTitle}>追加画像 {index + 1}</p>
                    <AdditionalMustDropzone field={field} />
                    {Array.isArray(errors.additional) && errors.additional[index] && errors.additional[index].message && (
                      <p className={styles.errorText}>{errors.additional[index].message}</p>
                    )}
                  </Layout>
                </div>
              );
            })
          )}
        </div>
        <Layout>
          <p className={styles.cautionText}>※画像が不鮮明などの理由で、追加の画像が必要な場合は後ほど画像送付をお願いする場合があります。</p>
        </Layout>
        <div className={styles.buttonWrapper}>
          {!isValid && (
            <p className={styles.alertMessage}>
              画像が不足しています
            </p>
          )}
          <button
            type="submit"
            className={`${global.primaryButton} js-trackEvent ${!isValid ? global.disabled : ''}`}
            data-ga-id="Clk_Btn_AppPage4_NextBtn"
            disabled={!isValid}
          >
            次へ進む
          </button>
          <div className={styles.operatorLabelWrapper}>
            <div className={styles.operatorLabelInner}>
              <p className={styles.operatorLabelTitle}>回答までの日数（目安）</p>
              <div className={styles.operatorLabel}>
                {expireText?.trim() && (
                  <div dangerouslySetInnerHTML={{ __html: expireText }} />
                )}
              </div>
            </div>
          </div>
          <div className={styles.operatorCautionWrapper}>
            <ul className={styles.operatorCautionList}>
              <li className={styles.operatorCautionItem}>
                1件1件スタッフが確認しているため日数をいただいております。お急ぎの場合は店舗にご相談ください。
              </li>
              <li className={styles.operatorCautionItem}>
                ご相談内容によっては回答まで日数がかかる場合があります。
              </li>
            </ul>
          </div>
        </div>
      </form>
    </div>
  );
};
